import { LocationInput, LaneInput, Maneuver } from '@kinetic-ui/data-layer';
import { IntersectionMapIcon } from '@kinetic-ui/data-layer';
import { RecursivePartial } from 'types';

export const getUnconnectedIds = (
  editingLane: RecursivePartial<LaneInput> | undefined,
  maneuvers: RecursivePartial<Maneuver>[],
  allLanes: RecursivePartial<LaneInput>[]
) => {
  if (!editingLane) {
    return [];
  }
  const connectedIds = new Set();
  const filteredLanes = allLanes.filter((lane) => editingLane.laneDirection !== lane.laneDirection);
  maneuvers.forEach((maneuver) => {
    if (
      maneuver.refApproachLane === editingLane.id ||
      maneuver.refDepartureLane === editingLane.id
    ) {
      connectedIds.add(maneuver.refApproachLane);
      connectedIds.add(maneuver.refDepartureLane);
    }
  });
  return filteredLanes.filter((lane) => !connectedIds.has(lane.id)).map((lane) => lane.id);
};

export const getManeuverId = (maneuver: RecursivePartial<Maneuver> | undefined) => {
  if (!maneuver?.refApproachLane || !maneuver?.refDepartureLane) {
    return '';
  }
  return maneuver.refApproachLane + maneuver.refDepartureLane;
};

export interface Arrow {
  head: number[];
  tail: number[];
}

const distance = (c1: number[], c2: number[]): number => {
  return Math.sqrt((c1[0] - c2[0]) ** 2 + (c1[1] - c2[1]) ** 2);
};

// turn an intersection lane's coordinates into a directionalized arrow
// the last argument is the bounding of the lane, approach or depart
const getArrow = (
  laneCoordinates: number[][],
  intersectionCenter: number[],
  approach: Boolean
): Arrow => {
  const distances = laneCoordinates.map((coord) => distance(coord, intersectionCenter));
  let closestIndex = +!(distances[0] < distances[1]);
  // flip this for departurn lanes
  if (!approach) {
    closestIndex = +!closestIndex;
  }
  const furthestIndex = +!closestIndex;
  return {
    tail: laneCoordinates[furthestIndex],
    head: laneCoordinates[closestIndex],
  };
};

// takes in ANY number and rolls it into a bearing:
// bearing: 0 -> 180 (positive) north to south clockwise,
//          0 -> -180 (negative) north to south counterclock
const convertToBearing = (x: number): number => {
  if (x <= 180 && x >= -180) {
    return x;
  }
  let modCircle = x % 360;
  if (modCircle < 0) {
    modCircle += 360;
  }
  if (modCircle <= 180) {
    return modCircle;
  }
  return modCircle - 360;
};

// bearings will be considered SIMILAR if they are
// within thresholdTheta degrees of each other.
// bearing: 0 -> 180 (positive) north to south clockwise,
//          0 -> -180 (negative) north to south counterclock
const similarBearing = (x: number, y: number, thresholdTheta: number): boolean => {
  // assert that x >= y, and if not then call this fx again swapping args
  if (x < y) {
    return similarBearing(y, x, thresholdTheta);
  }
  const diff = x - y;
  // either they are within the tolerance
  // or they are so far out of the tolerance because they cross the +/- line,
  // and are really within the tolerance
  if (diff <= thresholdTheta || diff >= 360 - thresholdTheta) {
    return true;
  }
  return false;
};

//
// see how the approach bearing lines up with the departure bearing
// if they are "similar" (within 22.5 degrees), maneuver is straight
//    45 +/- 22.5 degrees to the left is slight left
//    90 +/- 22.5 degrees to the left is left
//    45 +/- 22.5 degrees to the right is slight right
//    90 +/- (90-22.5) degrees to the right is right
//    otherwise uturn
const getDefaultArrow = (approachBearing: number, departBearing: number): IntersectionMapIcon => {
  if (similarBearing(approachBearing, departBearing, 22.5)) {
    return IntersectionMapIcon.Straightarrow;
  }
  const left = convertToBearing(approachBearing - 90);
  if (similarBearing(left, departBearing, 22.5)) {
    return IntersectionMapIcon.Leftarrow;
  }

  const slightRight = convertToBearing(approachBearing + 45);
  if (similarBearing(slightRight, departBearing, 22.5)) {
    return IntersectionMapIcon.Slightrightarrow;
  }

  const right = convertToBearing(approachBearing + 90);
  if (similarBearing(right, departBearing, 90 - 22.5)) {
    return IntersectionMapIcon.Rightarrow;
  }
  const slightLeft = convertToBearing(approachBearing - 45);
  if (similarBearing(slightLeft, departBearing, 22.5)) {
    return IntersectionMapIcon.Slightleftarrow;
  }
  return IntersectionMapIcon.Uturn;
};

// courtesy of math wizard Mark Richards
export const getDefaultArrowIcon = (
  approachLane: RecursivePartial<LaneInput> | undefined,
  departureLane: RecursivePartial<LaneInput> | undefined,
  location: RecursivePartial<LocationInput> | undefined
): IntersectionMapIcon => {
  if (!location?.geometry || !approachLane?.geometry || !departureLane?.geometry) {
    return IntersectionMapIcon.Straightarrow;
  }

  const arrowBearing = (arrow: Arrow): number => {
    const deltaX = arrow.head[0] - arrow.tail[0];
    const deltaY = arrow.head[1] - arrow.tail[1];

    if (deltaY === 0) {
      return deltaX > 0 ? 90 : -90;
    }

    if (deltaX === 0) {
      return deltaY > 0 ? 0 : 180;
    }

    const theta = (Math.atan(deltaY / deltaX) * 180) / Math.PI;

    if (deltaX > 0 && deltaY > 0) {
      return 90 - theta;
    }

    if (deltaX < 0 && deltaY > 0) {
      return -1 * (90 + theta);
    }

    if (deltaX < 0 && deltaY < 0) {
      return -1 * (90 + theta);
    }

    return 90 - theta;
  };

  // use the approach lane's coordinates for the approach arrow
  const approachArrow = getArrow(
    approachLane.geometry.coordinates,
    location.geometry.coordinates,
    true
  );
  const approachArrowBearing = arrowBearing(approachArrow);
  //
  // use the maneuver's departure lane's coordinates for the depart arrow
  const departureArrow = getArrow(
    departureLane.geometry.coordinates,
    location.geometry.coordinates,
    false
  );
  const departureArrowBearing = arrowBearing(departureArrow);
  // fetches the default icon for a movement given approach and depart arrows
  return getDefaultArrow(approachArrowBearing, departureArrowBearing);
};
